import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AppBar, Toolbar, Box } from '@mui/material';

import Login from './Login';
import HomePage from './Home';
import AskDrBill from './AskDrBill';
import BenefitsConcierge from './BenefitsConcierge';
import AccountInfo from './AccountInfo';
import Callback from './Callback';
import NavTabs from './NavTabs';
import { getTenantConfig } from './theme/tenantConfigs';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import withAuthenticationRequired from './withAuthenticationRequired';
import UnauthorizedPage from './UnauthorizedPage';
import { useUserMetadata } from './useUserMetadata';

// Define required roles
const requiredRoles = ['mobile-app-user', 'admin'];

// Create protected components 
const ProtectedHome = withAuthenticationRequired(HomePage, requiredRoles);
const ProtectedAskDrBill = withAuthenticationRequired(AskDrBill, requiredRoles);
const ProtectedBenefitsConcierge = withAuthenticationRequired(BenefitsConcierge, requiredRoles);
const ProtectedAccountInfo = withAuthenticationRequired(AccountInfo, requiredRoles);

function App() {
  const { isAuthenticated, user } = useAuth0();
  const { userMetadata } = useUserMetadata();
  const tenant = getTenantConfig();

  // Debug log to see what's happening
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('App: User is authenticated:', user);
      console.log('App: User metadata:', userMetadata);
      
      // Check specific namespace for metadata
      const namespaceMetadata = user['https://metadata.congruityhealth.com/user_metadata'];
      console.log('App: Namespace metadata:', namespaceMetadata);
      
      // Log roles from metadata if available
      if (userMetadata?.Roles) {
        console.log('App: User roles:', userMetadata.Roles);
      } else {
        console.log('App: No roles found in userMetadata');
      }
    }
  }, [isAuthenticated, user, userMetadata]);

  return (
    <Box sx={{ pb: 7 }}>
      {/* Only show AppBar if user is authenticated */}
      {isAuthenticated && (
        <AppBar
          position="sticky"
          sx={{
            height: '7vh',
            background: tenant.gradient
          }}
        >
          <Toolbar style={{ height: "7vh", marginRight: 8, alignItems: 'flex-start', paddingTop: "1.2vh" }}>
            <img
              src={tenant.logo}
              alt="Tenant Logo"
              style={{ height: "4.75vh", marginRight: 8 }}
            />
          </Toolbar>
        </AppBar>
      )}

      <Routes>
        <Route path="/login" element={<Login />} />
        
        {/* Let the HOC handle both authentication and authorization */}
        <Route path="/" element={<ProtectedHome />} />
        <Route path="/ask-dr-bill" element={<ProtectedAskDrBill />} />
        <Route path="/benefits-concierge" element={<ProtectedBenefitsConcierge />} />
        <Route path="/account" element={<ProtectedAccountInfo />} />
        
        <Route path="/callback" element={<Callback />} />
        <Route path="/Unauthorized" element={<UnauthorizedPage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      {/* Show bottom nav tabs only if user is authenticated */}
      {isAuthenticated && <NavTabs />}
      <PWAInstallPrompt />
    </Box>
  );
}

export default App;